import React, { useEffect, useMemo, useRef, useState } from "react";
import Select, { StylesConfig } from "react-select";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";
import { useQueryClient } from "react-query";
import { BasePage } from "components/BasePage/BasePage";
import { copyErrorMessage, copyOperationKey, useCopyDiscovery, usePreviewProjectCopy, useProjectCopy } from "hooks/useProjectCopy";
import { projectCopyText } from "./text";

const buttonClass = "px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed";
const selectClass = "mt-1 block w-full rounded-md border-gray-300";

interface AccountOption {
  value: string;
  label: string;
}

const accountSelectStyles: StylesConfig<AccountOption, false> = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? "#06b6d4" : "#d1d5db",
    borderRadius: "0.375rem",
    boxShadow: state.isFocused ? "0 0 0 1px #06b6d4" : "none",
  }),
  input: (base) => ({
    ...base,
    "input:focus": { boxShadow: "none" },
  }),
};

export const ProjectCopyBackOffice = () => {
  const { t } = useTranslation();
  const text = (key: keyof typeof projectCopyText) => t(`backoffice.projectCopy.${key}`, projectCopyText[key]);
  const history = useHistory();
  const location = useLocation();
  const id = new URLSearchParams(location.search).get("operation") || "";
  const locationRef = useRef(location);
  locationRef.current = location;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const selectionRevision = useRef(0);
  const previewLock = useRef(false);
  const queryClient = useQueryClient();
  const preview = usePreviewProjectCopy();
  const { operation, pending, uncertain, actionError, execute, refresh } = useProjectCopy(id);
  const [previewError, setPreviewError] = useState("");
  const [paused, setPaused] = useState(false);
  const [confirmCleanup, setConfirmCleanup] = useState(false);
  useEffect(() => { setPaused(false); setConfirmCleanup(false); setPreviewError(""); }, [id]);

  const setOperation = (operationId: string) => {
    const params = new URLSearchParams(locationRef.current.search);
    if (operationId) params.set("operation", operationId);
    else params.delete("operation");
    history.replace({ ...locationRef.current, search: params.toString() ? `?${params}` : "" });
  };
  const form = useFormik({
    initialValues: { sourceAccount: "", source: "", destinationAccount: "" },
    onSubmit: async (values) => {
      if (previewLock.current || pending || uncertain || !values.source ||
        !values.sourceAccount || !values.destinationAccount || values.sourceAccount === values.destinationAccount) return;
      previewLock.current = true;
      const revision = selectionRevision.current;
      const submittedLocation = locationRef.current;
      setPreviewError("");
      try {
        const result = await preview.mutateAsync({ source: values.source, destinationAccount: values.destinationAccount });
        if (!mounted.current || revision !== selectionRevision.current || submittedLocation !== locationRef.current) return;
        queryClient.setQueryData(copyOperationKey(result.id), result);
        setOperation(result.id);
      } catch (error) {
        if (mounted.current && revision === selectionRevision.current && submittedLocation === locationRef.current) setPreviewError(copyErrorMessage(error));
      } finally { previewLock.current = false; }
    },
  });
  const { users, projects } = useCopyDiscovery(form.values.sourceAccount);
  const accounts = useMemo<AccountOption[]>(() => {
    const emailsByAccount = new Map<string, Set<string>>();
    for (const user of users.data || []) {
      if (!user.accountId) continue;
      const emails = emailsByAccount.get(user.accountId) || new Set<string>();
      if (user.email) emails.add(user.email);
      emailsByAccount.set(user.accountId, emails);
    }
    return Array.from(emailsByAccount.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([value, emails]) => ({ value, label: [value, ...emails].join(" · ") }));
  }, [users.data]);
  const busy = preview.isLoading || pending;
  const blocked = busy || uncertain || operation.isFetching || operation.isError;
  const accountSelectProps = {
    options: accounts,
    isSearchable: true,
    isClearable: true,
    isDisabled: busy || uncertain || users.isLoading,
    isLoading: users.isLoading,
    placeholder: text("accountSearch"),
    noOptionsMessage: () => text("noAccounts"),
    filterOption: (option: { label: string }, input: string) =>
      option.label.toLocaleLowerCase().includes(input.trim().toLocaleLowerCase()),
    styles: accountSelectStyles,
    className: "mt-1 sm:text-sm",
  };
  const valid = !!form.values.source && !!form.values.sourceAccount &&
    !!form.values.destinationAccount && form.values.sourceAccount !== form.values.destinationAccount;
  const change = (name: keyof typeof form.values, value: string) => {
    ++selectionRevision.current;
    const values = { ...form.values, [name]: value };
    if (name === "sourceAccount") values.source = "";
    form.setValues(values);
    setPreviewError("");
    setPaused(false);
    setConfirmCleanup(false);
    if (id) setOperation("");
  };
  const summary = operation.data;
  const warnings = summary?.warnings ?? [];
  const statusKeys = { ready: "ready", copy: "copy", failed: "failed", complete: "complete", cleanup: "cleanupStatus", "cleanup-failed": "cleanupFailed", cleaned: "cleaned" } as const;
  const discoveryErrors = [users.error, projects.error].filter(Boolean);
  return (
    <BasePage breadcrumbItems={[{ label: "Backoffice", location: "/_backoffice" }, { label: text("title"), location: "/_backoffice/_project-copy" }]}>
      <div className="max-w-4xl space-y-6">
        <h1 className="text-2xl font-semibold text-gray-900">{text("title")}</h1>
        <p>{text("intro")}</p>
        <form onSubmit={form.handleSubmit} className="bg-white border rounded-lg p-6 space-y-4">
          <fieldset disabled={busy || uncertain} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="project-copy-sourceAccount">{text("sourceAccount")}</label>
              <Select<AccountOption, false>
                {...accountSelectProps}
                inputId="project-copy-sourceAccount"
                instanceId="project-copy-sourceAccount"
                name="sourceAccount"
                value={accounts.find((account) => account.value === form.values.sourceAccount) || null}
                onChange={(account) => change("sourceAccount", account?.value || "")}
              />
            </div>
            <label>{text("source")}<select className={selectClass} value={form.values.source} disabled={!form.values.sourceAccount || projects.isLoading} onChange={(e) => change("source", e.target.value)}>
              <option value="">{text("choose")}</option>{projects.data?.map((project) => <option key={project.crn} value={project.crn}>{project.name || project.crn}</option>)}
            </select></label>
            <div>
              <label htmlFor="project-copy-destinationAccount">{text("destinationAccount")}</label>
              <Select<AccountOption, false>
                {...accountSelectProps}
                inputId="project-copy-destinationAccount"
                instanceId="project-copy-destinationAccount"
                name="destinationAccount"
                value={accounts.find((account) => account.value === form.values.destinationAccount) || null}
                onChange={(account) => change("destinationAccount", account?.value || "")}
              />
            </div>

          </fieldset>
          {!!form.values.sourceAccount && form.values.sourceAccount === form.values.destinationAccount && <p role="alert">{text("differentAccounts")}</p>}
          {discoveryErrors.map((error, index) => <p role="alert" key={index}>{copyErrorMessage(error)}</p>)}
          <p>{text("fullHistory")}</p>
          <button className={buttonClass} type="submit" disabled={!valid || busy || uncertain || !!discoveryErrors.length}>{text(preview.isLoading ? "previewing" : "preview")}</button>
        </form>
        {previewError && <p role="alert">{text("previewFailed")} {previewError}</p>}
        {id && <section aria-label={text("operation")} className="bg-white border rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-semibold">{text("operation")}: {id}</h2>
          {summary && <>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 break-all">
              {([["source", summary.source], ["destination", summary.destination], ["destinationAccount", summary.destinationAccount], ["status", text(statusKeys[summary.status])], ["fileCount", summary.fileCount], ["totalBytes", summary.totalBytes], ["recordCount", summary.recordCount]] as const).map(([label, value]) => <div key={label}><dt className="font-medium">{text(label)}</dt><dd>{value}</dd></div>)}
            </dl>
            {!!warnings.length && <div><h3 className="font-medium">{text("warnings")}</h3><ul className="list-disc pl-5">{warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul></div>}
          </>}
          {pending && <p role="status">{text("working")}</p>}
          {uncertain && !pending && <p role="alert">{text("unknown")}</p>}
          {operation.isFetching && !pending && <p role="status">{text("checking")}</p>}
          {actionError && <p role="alert">{actionError}</p>}
          {!!operation.error && <p role="alert">{copyErrorMessage(operation.error)}</p>}
          <button type="button" className={buttonClass} disabled={busy || operation.isFetching} onClick={() => refresh()}>{text("refresh")}</button>
          {summary && summary.status !== "complete" && <>
            <label className="block"><input type="checkbox" checked={paused} disabled={busy} onChange={(e) => setPaused(e.target.checked)} className="mr-2" />{text("paused")}</label>
            <div className="flex flex-wrap gap-3">
              <button type="button" className={buttonClass} disabled={blocked || !paused || !summary.canApply} onClick={() => execute("apply")}>{text(summary.status === "ready" ? "apply" : "retry")}</button>
              <button type="button" className={buttonClass} disabled={blocked || !summary.canCleanup} onClick={() => setConfirmCleanup(true)}>{text("cleanup")}</button>
            </div>
            {confirmCleanup && <div role="dialog" aria-label={text("cleanup")} className="border border-red-300 rounded p-4 space-y-3">
              <p>{text("cleanupConfirm")}</p>
              <button type="button" className={buttonClass} disabled={blocked || !summary.canCleanup} onClick={() => { setConfirmCleanup(false); execute("cleanup"); }}>{text("confirmCleanup")}</button>{" "}
              <button type="button" className={buttonClass} onClick={() => setConfirmCleanup(false)}>{text("cancel")}</button>
            </div>}
          </>}
          {summary?.status === "complete" && <div className="space-y-2"><Link className="text-blue-600 underline" to={`/projects/${summary.destination}`}>{text("open")}</Link><p>{text("access")}</p></div>}
        </section>}
      </div>
    </BasePage>
  );
};
